import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import BuyMeACoffeeButton from '../components/BuyMeACoffeeButton';
import '../App.css';
import './Page.css';

function SunIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="2"/>
      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

export default function Imprint() {
  const [theme, setTheme] = useState('dark');
  const [logoSrc, setLogoSrc] = useState('/logo.png');
  const [isPageLoaded, setIsPageLoaded] = useState(false);
  const location = useLocation();

  useEffect(() => {
    // Get initial theme: check localStorage first, then browser preference, default to dark
    const getInitialTheme = () => {
      const savedTheme = localStorage.getItem('theme');
      if (savedTheme) {
        return savedTheme;
      }
      // Check browser preference
      if (window.matchMedia && window.matchMedia('(prefers-color-scheme: light)').matches) {
        return 'light';
      }
      return 'dark';
    };
    
    const initialTheme = getInitialTheme();
    setTheme(initialTheme);
    document.documentElement.className = initialTheme === 'light' ? 'light' : '';
    
    // Trigger page load animation
    setIsPageLoaded(true);
  }, []);

  const toggleTheme = () => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    document.documentElement.className = newTheme === 'light' ? 'light' : '';
  };

  const handleLogoError = () => {
    if (logoSrc !== '/logo.svg') setLogoSrc('/logo.svg');
  };

  const footerLogo = '/luther.png';
  const toggleTooltip = theme === 'dark' ? 'Light Mode' : 'Dark Mode';
  const refreshPage = () => window.location.href = '/';
  const onBrandKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      refreshPage();
    }
  };

  return (
    <div className="app-container">
      <header className={`app-header ${isPageLoaded ? 'fade-in-blur' : ''}`}>
        <div className="header-inner">
          <div 
            className="brand" 
            onClick={refreshPage}
            onKeyDown={onBrandKeyDown}
            tabIndex={0}
            role="button"
            aria-label="Go to home"
          >
            <div className="brand-logo-wrapper">
              <img src={logoSrc} alt="Colority" className="brand-logo-blur" onError={handleLogoError} />
              <img src={logoSrc} alt="Colority" className="brand-logo" onError={handleLogoError} />
            </div>
            <h1 className="app-title">Colority</h1>
          </div>
          <button
            className="theme-toggle"
            onClick={toggleTheme}
            aria-label={toggleTooltip}
            data-tooltip={toggleTooltip}
          >
            {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
          </button>
        </div>
      </header>
      <div className="page-container">
        <div className="page-content">
        <h1>Imprint</h1>
        
        <section>
          <h2>Information in accordance with Section 5 TMG</h2>
          <p>
            Niklas Luther<br />
            Dompfaffweg 16<br />
            89269 Vöhringen, Germany
          </p>
        </section>

        <section>
          <h2>Contact Information</h2>
          <p>
            E-Mail: <a href="mailto:hello@luther.studio" target="_blank" rel="noopener noreferrer">hello@luther.studio</a><br />
            Website: <a href="https://colority.net" target="_blank" rel="noopener noreferrer">https://colority.net</a><br />
            VAT-ID: DE327430586
          </p>
        </section>

        <section>
          <h2>Disclaimer</h2>
          <h2>Accountability for content</h2>
          <p>
            The contents of our pages have been created with the utmost care. However, we cannot guarantee the accuracy, completeness or timeliness of the content. Pursuant to statutory provisions, we are responsible for our own content on these pages. In this context, please note that we are not obliged to monitor transmitted or stored information of third parties or to investigate circumstances indicating illegal activity (§§ 8–10 TMG).
          </p>
        </section>

        <section>
          <h2>Accountability for links</h2>
          <p>
            Responsibility for the content of external links lies solely with the operators of the linked pages. At the time of linking, no violations were evident. Upon notification of legal violations, such links will be removed immediately.
          </p>
        </section>

        <section>
          <h2>Copyright</h2>
          <p>
            The content and works on these pages are subject to German copyright law. Any use, reproduction or processing outside the limits of copyright requires prior written consent of the respective rights holder. Copies of these pages are permitted only for private use.
          </p>
        </section>

        <section>
          <h2>Privacy Policy</h2>
          <p>
            For information on data protection, please see our <Link to="/privacy-policy">Privacy Policy</Link>
          </p>
        </section>

        </div>
      </div>
      <footer className={`app-footer ${isPageLoaded ? 'fade-in-blur fade-delay-3' : ''}`}>
        <div className="footer-top-row">
          <div className="footer-left">
            <div className="footer-left-desktop-row">
              <div className="footer-left-top">
                <img className="footer-logo" src={footerLogo} alt="Luther Studio" />
                <span>By <a href="https://www.luther.studio/index.html" className="footer-brand-link" rel="noopener noreferrer" target="_blank">Luther Studio</a> © 2025</span>
              </div>
              <div className="footer-links-group">
                <Link to="/imprint" className={`footer-link ${location.pathname === '/imprint' ? 'active' : ''}`}>Imprint</Link>
                <Link to="/privacy-policy" className={`footer-link ${location.pathname === '/privacy-policy' ? 'active' : ''}`}>Privacy Policy</Link>
              </div>
            </div>
          </div>
          <div className="footer-right">
            <BuyMeACoffeeButton theme={theme} />
            <a 
              href="https://www.producthunt.com/products/colority-net?embed=true&utm_source=badge-featured&utm_medium=badge&utm_source=badge-colority" 
              target="_blank" 
              rel="noopener noreferrer"
              className="producthunt-widget"
            >
              <img 
                src={`https://api.producthunt.com/widgets/embed-image/v1/featured.svg?post_id=1045069&theme=${theme}&t=1764866734409`}
                alt="Colority - Extract colors from images | Product Hunt" 
                className="producthunt-badge"
              />
            </a>
          </div>
        </div>
        <div className="footer-bottom-row">
          <BuyMeACoffeeButton className="bmc-mobile" theme={theme} />
          <a 
            href="https://www.producthunt.com/products/colority-net?embed=true&utm_source=badge-featured&utm_medium=badge&utm_source=badge-colority" 
            target="_blank" 
            rel="noopener noreferrer"
            className="producthunt-widget producthunt-mobile"
          >
            <img 
              src={`https://api.producthunt.com/widgets/embed-image/v1/featured.svg?post_id=1045069&theme=${theme}&t=1764866734409`}
              alt="Colority - Extract colors from images | Product Hunt" 
              className="producthunt-badge"
            />
          </a>
        </div>
      </footer>
    </div>
  );
}

